import { useRef, useState } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { PenLine, Type, Eraser } from 'lucide-react';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (name: string, signatureDataUrl: string) => Promise<void>;
  defaultName?: string;
}

export function SignatureModal({ open, onOpenChange, onSubmit, defaultName = '' }: Props) {
  const sigRef = useRef<SignatureCanvas | null>(null);
  const [mode, setMode] = useState<'draw' | 'type'>('draw');
  const [name, setName] = useState(defaultName);
  const [typedSignature, setTypedSignature] = useState(defaultName);
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const clear = () => sigRef.current?.clear();

  const renderTypedToDataUrl = (text: string): string => {
    const canvas = document.createElement('canvas');
    canvas.width = 600;
    canvas.height = 200;
    const ctx = canvas.getContext('2d');
    if (!ctx) return '';
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = '#111827';
    ctx.font = "italic 64px 'Brush Script MT', 'Dancing Script', cursive";
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(text || ' ', canvas.width / 2, canvas.height / 2);
    return canvas.toDataURL('image/png');
  };

  const handleSubmit = async () => {
    if (!name.trim() || !agreed) return;
    let data = '';
    if (mode === 'draw') {
      if (!sigRef.current || sigRef.current.isEmpty()) return;
      data = sigRef.current.getCanvas().toDataURL('image/png');
    } else {
      data = renderTypedToDataUrl(typedSignature || name);
    }
    setSubmitting(true);
    try {
      await onSubmit(name.trim(), data);
      onOpenChange(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Approve & Sign Proposal</DialogTitle>
          <DialogDescription>
            Your signature confirms your agreement to the proposal terms.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          <div>
            <Label htmlFor="signer-name">Full legal name</Label>
            <Input
              id="signer-name"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (mode === 'type') setTypedSignature(e.target.value);
              }}
              placeholder="Jane Smith"
              className="mt-1.5"
            />
          </div>

          <Tabs value={mode} onValueChange={(v) => setMode(v as 'draw' | 'type')}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="draw" className="gap-2">
                <PenLine className="w-4 h-4" /> Draw
              </TabsTrigger>
              <TabsTrigger value="type" className="gap-2">
                <Type className="w-4 h-4" /> Type
              </TabsTrigger>
            </TabsList>

            <TabsContent value="draw" className="mt-3">
              <div className="border-2 border-dashed border-border rounded-lg bg-white overflow-hidden">
                <SignatureCanvas
                  ref={sigRef}
                  canvasProps={{ className: 'w-full h-48', style: { width: '100%', height: '192px' } }}
                  backgroundColor="#ffffff"
                  penColor="#111827"
                />
              </div>
              <div className="mt-2 flex justify-end">
                <Button variant="ghost" size="sm" onClick={clear} className="gap-2">
                  <Eraser className="w-3.5 h-3.5" /> Clear
                </Button>
              </div>
            </TabsContent>

            <TabsContent value="type" className="mt-3">
              <div className="border-2 border-dashed border-border rounded-lg bg-white h-48 flex items-center justify-center px-4">
                <span
                  className="text-4xl text-gray-900 truncate"
                  style={{ fontFamily: "'Brush Script MT', 'Dancing Script', cursive", fontStyle: 'italic' }}
                >
                  {typedSignature || name || 'Your signature'}
                </span>
              </div>
              <Input
                value={typedSignature}
                onChange={(e) => setTypedSignature(e.target.value)}
                placeholder="Type your signature"
                className="mt-2"
              />
            </TabsContent>
          </Tabs>

          <div className="flex items-start gap-3 p-4 rounded-lg bg-muted/50 border border-border">
            <Checkbox id="agree" checked={agreed} onCheckedChange={(v) => setAgreed(v === true)} className="mt-0.5" />
            <Label htmlFor="agree" className="text-sm font-normal leading-relaxed cursor-pointer">
              I agree that this signature constitutes a legally binding agreement and have authority to sign on behalf of the named party.
            </Label>
          </div>

          <div className="flex items-center justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={submitting || !name.trim() || !agreed}
              className="bg-workspace-accent hover:bg-workspace-accent/90 text-white"
            >
              {submitting ? 'Signing...' : 'Approve & Sign'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
